import { mixed, object, string } from "yup"
import {
  ACCOUNT_HOLDER_NAME_MAX_LENGTH,
  ACCOUNT_NUMBER_MAX_LENGTH,
  ROUTING_NUMBER_LENGTH,
  TOKEN_TYPE,
} from "../../../../ach/constants"
import { BankAccountEnumType } from "@oysterhr/schema"
import { Stripe, Token } from "@stripe/stripe-js"
import { type AddAchFormValues } from "../../../types"
import { captureMessage } from "@sentry/react"
import { TFunction } from "i18next"

export const createAchFormValidationSchema = (t: TFunction) => {
  return object().shape({
    country: string(),
    currencyCode: string(),
    routingNumber: string()
      .trim()
      .required(t("required.routingNumber"))
      .matches(/^\d+$/, t("validation.number"))
      .min(ROUTING_NUMBER_LENGTH, t("validation.digits", { digits: ROUTING_NUMBER_LENGTH }))
      .max(ROUTING_NUMBER_LENGTH, t("validation.digits", { digits: ROUTING_NUMBER_LENGTH })),
    accountNumber: string()
      .trim()
      .required(t("required.accountNumber"))
      .max(
        ACCOUNT_NUMBER_MAX_LENGTH,
        t("validation.max", { characters: ACCOUNT_NUMBER_MAX_LENGTH }),
      ),
    accountHolderName: string()
      .trim()
      .required(t("required.accountHolderName"))
      .max(
        ACCOUNT_HOLDER_NAME_MAX_LENGTH,
        t("validation.digits", { digits: ACCOUNT_HOLDER_NAME_MAX_LENGTH }),
      ),
    bankAccountType: mixed()
      .oneOf(Object.values(BankAccountEnumType))
      .required(t("required.bankAccountType")),
  })
}

export const getStripeToken = async (
  stripe: Stripe | null,
  values: AddAchFormValues,
): Promise<Token> => {
  if (!stripe) {
    throw new Error("Stripe isn't ready")
  }

  const { token, error } = await stripe.createToken(TOKEN_TYPE, {
    country: values.country,
    currency: values.currencyCode,
    routing_number: values.routingNumber,
    account_number: values.accountNumber,
    account_holder_name: values.accountHolderName,
    account_holder_type: "company",
  })

  if (error) {
    captureMessage(error.message || "Couldn't submit ACH direct debit details", {
      extra: {
        ...error,
      },
    })
    throw new Error(error.message)
  }

  return token
}
